import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    Box,
    Typography,
    Popover,
    Button,
    IconButton,
    Grid
} from '@mui/material';
import { CalendarTodayOutlinedIcon, CloseIcon, KeyboardArrowLeftIcon, KeyboardArrowRightIcon } from 'icons/materialIcons';
import WhiteButtonWithIcon from 'components/common/button/WhiteButtonWithIcon';
import PropTypes from 'prop-types';
import {
    formatDate,
    formatFullDate,
    formatMonthYear,
    isSameDay,
    isInRange,
    addMonths,
    generateCalendarDays
} from 'utils/dateUtils';

const CalendarFilter = ({ onDateRangeChange, initialDateRange = null }) => {
    const dateButtonRef = useRef(null);

    // Default date range - initially set to all time
    const [dateRange, setDateRange] = useState(
        initialDateRange || [new Date('2024-01-01'), new Date()]
    );

    // Current calendar view - month being displayed
    const [calendarViewDate, setCalendarViewDate] = useState(new Date());

    // State for date picker popover
    const [datePopoverOpen, setDatePopoverOpen] = useState(false);
    const [tempDateRange, setTempDateRange] = useState(dateRange);
    const [currentStep, setCurrentStep] = useState(0); // 0 = start date, 1 = end date

    // Current calendar display
    const [calendarDays, setCalendarDays] = useState([]);

    // Update calendar when view date changes
    useEffect(() => {
        const year = calendarViewDate.getFullYear();
        const month = calendarViewDate.getMonth();
        setCalendarDays(generateCalendarDays(year, month));
    }, [calendarViewDate]);

    const getPredefinedRanges = () => {
        const today = new Date();

        const daysAgo = (days) => {
            const date = new Date(today);
            date.setDate(date.getDate() - days);
            return date;
        };

        const firstDayOfMonth = (monthsAgo = 0) => {
            const date = new Date(today);
            date.setMonth(date.getMonth() - monthsAgo);
            date.setDate(1);
            return date;
        };

        const lastDayOfMonth = (monthsAgo = 1) => {
            const date = new Date(today);
            date.setMonth(date.getMonth() - monthsAgo + 1);
            date.setDate(0);
            return date;
        };

        return [
            { label: 'Today', range: [today, today] },
            { label: 'Yesterday', range: [daysAgo(1), daysAgo(1)] },
            { label: 'Last 7 days', range: [daysAgo(6), today] },
            { label: 'Last 30 days', range: [daysAgo(29), today] },
            { label: 'This month', range: [firstDayOfMonth(), today] },
            { label: 'Last month', range: [firstDayOfMonth(1), lastDayOfMonth(1)] },
            { label: 'All time', range: [new Date('2024-01-01'), today] }
        ];
    };

    const predefinedRanges = getPredefinedRanges();

    // Helper function to check if two date ranges match (ignoring time)
    const isDateRangeMatch = (range1, range2) => {
        if (!range1 || !range2 || !range1[0] || !range1[1] || !range2[0] || !range2[1]) {
            return false;
        }

        // Normalize dates to start of day for comparison
        const normalizeDate = (date) => {
            const normalized = new Date(date);
            normalized.setHours(0, 0, 0, 0);
            return normalized.getTime();
        };

        const range1Start = normalizeDate(range1[0]);
        const range1End = normalizeDate(range1[1]);
        const range2Start = normalizeDate(range2[0]);
        const range2End = normalizeDate(range2[1]);

        return range1Start === range2Start && range1End === range2End;
    };

    const handleDateRangeClick = (event) => {
        setDatePopoverOpen(true);
        dateButtonRef.current = event.currentTarget;
        setTempDateRange(dateRange);
        setCurrentStep(0);
        setCalendarViewDate(new Date());
    };

    const handleDatePopoverClose = () => {
        setDatePopoverOpen(false);
    };

    const handleApplyDateRange = () => {
        let sortedRange = [...tempDateRange];
        if (tempDateRange[0] > tempDateRange[1]) {
            sortedRange = [tempDateRange[1], tempDateRange[0]];
        }

        const adjustedEndDate = new Date(sortedRange[1]);
        adjustedEndDate.setHours(23, 59, 59, 999);

        const finalRange = [
            sortedRange[0],
            adjustedEndDate
        ];

        setDateRange(finalRange);
        setDatePopoverOpen(false);

        if (onDateRangeChange) {
            onDateRangeChange(finalRange[0], finalRange[1]);
        }
    };

    const handleResetDateRange = () => {
        const allTimeRange = predefinedRanges.find(r => r.label === 'All time').range;
        setTempDateRange(allTimeRange);
        setDateRange(allTimeRange);
        setDatePopoverOpen(false);

        if (onDateRangeChange) {
            onDateRangeChange(allTimeRange[0], allTimeRange[1]);
        }
    };

    const handleSelectPredefinedRange = (range) => {
        const adjustedEndDate = new Date(range[1]);
        adjustedEndDate.setHours(23, 59, 59, 999);

        const adjustedRange = [range[0], adjustedEndDate];

        setTempDateRange(adjustedRange);
        setCurrentStep(1);
    };

    const handleDateSelect = (date) => {
        if (!date) return;

        if (currentStep === 0) {
            setTempDateRange([date, tempDateRange[1]]);
            setCurrentStep(1);
        } else {
            setTempDateRange([tempDateRange[0], date]);
        }
    };

    const handlePrevMonth = () => {
        setCalendarViewDate(addMonths(calendarViewDate, -1));
    };

    const handleNextMonth = () => {
        setCalendarViewDate(addMonths(calendarViewDate, 1));
    };

    // Check if current range matches "Last 30 days"
    const isLast30Days = () => {
        if (!dateRange[0] || !dateRange[1]) return false;
        const today = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(today.getDate() - 29); // 30 days including today

        return isSameDay(dateRange[0], thirtyDaysAgo) && isSameDay(dateRange[1], today);
    };

    const dateRangeText = dateRange[0] && dateRange[1]
        ? (isLast30Days()
            ? `Last 30 days ${formatDate(dateRange[0])} - ${formatDate(dateRange[1])}`
            : `${formatDate(dateRange[0])} - ${formatDate(dateRange[1])}`)
        : 'All time';

    const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

    return (
        <>
            <WhiteButtonWithIcon
                ref={dateButtonRef}
                icon={CalendarTodayOutlinedIcon}
                text={dateRangeText}
                onClick={handleDateRangeClick}
                sx={{
                    '& .MuiButton-startIcon': {
                        marginRight: '8px'
                    }
                }}
            />

            <Popover
                open={datePopoverOpen}
                anchorEl={dateButtonRef.current}
                onClose={handleDatePopoverClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                PaperProps={{
                    sx: {
                        width: 560,
                        borderRadius: '16px',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                        padding: 0,
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
                        Select Date Range
                    </Typography>
                    <IconButton size="small" onClick={handleDatePopoverClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Box sx={{ display: 'flex' }}>
                    <Box sx={{
                        width: '160px',
                        borderRight: '1px solid #e0e0e0',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                    }}>
                        <Typography variant="subtitle2" sx={{ fontSize: '14px', fontWeight: 600, mb: 1 }}>
                            Preset Ranges
                        </Typography>

                        {predefinedRanges.map((preset, index) => {
                            // When popover is open, check tempDateRange (what user is currently selecting)
                            // When popover is closed, check dateRange (what is actually applied)
                            const isSelected = datePopoverOpen
                                ? isDateRangeMatch(tempDateRange, preset.range)
                                : isDateRangeMatch(dateRange, preset.range);
                            return (
                                <Button
                                    key={index}
                                    onClick={() => handleSelectPredefinedRange(preset.range)}
                                    variant="text"
                                    size="small"
                                    sx={{
                                        justifyContent: 'flex-start',
                                        textTransform: 'none',
                                        color: isSelected ? '#000' : '#666',
                                        fontWeight: isSelected ? 600 : 400,
                                        backgroundColor: isSelected ? 'rgba(41, 174, 248, 0.1)' : 'transparent',
                                        borderLeft: isSelected ? '3px solid #29AEF8' : '3px solid transparent',
                                        padding: '4px 8px',
                                        paddingLeft: isSelected ? '5px' : '8px',
                                        minHeight: '32px',
                                        fontSize: '13px',
                                        '&:hover': {
                                            backgroundColor: isSelected ? 'rgba(41, 174, 248, 0.15)' : 'rgba(0,0,0,0.05)'
                                        }
                                    }}
                                >
                                    {preset.label}
                                </Button>
                            );
                        })}
                    </Box>

                    <Box sx={{ flex: 1, padding: '0' }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px 16px',
                            borderBottom: '1px solid #e0e0e0'
                        }}>
                            <IconButton size="small" onClick={handlePrevMonth}>
                                <KeyboardArrowLeftIcon fontSize="small" />
                            </IconButton>
                            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                {formatMonthYear(calendarViewDate)}
                            </Typography>
                            <IconButton size="small" onClick={handleNextMonth}>
                                <KeyboardArrowRightIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        <Box sx={{ padding: '16px' }}>
                            <Grid container>
                                {weekDays.map((day, index) => (
                                    <Grid item xs={12 / 7} key={index} sx={{ textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>
                                            {day}
                                        </Typography>
                                    </Grid>
                                ))}
                            </Grid>

                            <Grid container sx={{ mt: 1 }}>
                                {calendarDays.map((dayObj, index) => (
                                    <Grid item xs={12 / 7} key={index}>
                                        {dayObj.day !== null && (
                                            <Box
                                                onClick={() => handleDateSelect(dayObj.date)}
                                                sx={{
                                                    height: '40px',
                                                    width: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    position: 'relative',
                                                    borderRadius: '50%',
                                                    margin: '4px auto',

                                                    ...(tempDateRange[0] && isSameDay(dayObj.date, tempDateRange[0]) && {
                                                        backgroundColor: '#000',
                                                        color: '#fff',
                                                    }),

                                                    ...(tempDateRange[1] && isSameDay(dayObj.date, tempDateRange[1]) && {
                                                        backgroundColor: '#000',
                                                        color: '#fff',
                                                    }),

                                                    ...(tempDateRange[0] && tempDateRange[1] &&
                                                        isInRange(dayObj.date, tempDateRange[0], tempDateRange[1]) && {
                                                        backgroundColor: 'rgba(0,0,0,0.1)',
                                                    }),

                                                    ...(isSameDay(dayObj.date, new Date()) && !isSameDay(dayObj.date, tempDateRange[0]) &&
                                                        !isSameDay(dayObj.date, tempDateRange[1]) && {
                                                        border: '1px solid #000',
                                                    }),

                                                    '&:hover': {
                                                        backgroundColor:
                                                            (tempDateRange[0] && isSameDay(dayObj.date, tempDateRange[0])) ||
                                                                (tempDateRange[1] && isSameDay(dayObj.date, tempDateRange[1]))
                                                                ? '#000' : 'rgba(0,0,0,0.2)',
                                                        color:
                                                            (tempDateRange[0] && isSameDay(dayObj.date, tempDateRange[0])) ||
                                                                (tempDateRange[1] && isSameDay(dayObj.date, tempDateRange[1]))
                                                                ? '#fff' : 'inherit',
                                                    },
                                                }}
                                            >
                                                <Typography sx={{ fontSize: '14px' }}>
                                                    {dayObj.day}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{
                    padding: '16px',
                    borderTop: '1px solid #e0e0e0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="caption" sx={{ color: '#666', fontSize: '12px' }}>
                                Start Date
                            </Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                                {tempDateRange[0] ? formatFullDate(tempDateRange[0]) : 'Select date'}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="caption" sx={{ color: '#666', fontSize: '12px' }}>
                                End Date
                            </Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                                {tempDateRange[1] ? formatFullDate(tempDateRange[1]) : 'Select date'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Button
                            onClick={handleResetDateRange}
                            variant="outlined"
                            size="small"
                            sx={{
                                borderColor: '#e0e0e0',
                                color: '#666',
                                textTransform: 'none',
                                fontSize: '13px',
                                borderRadius: '20px',
                                '&:hover': {
                                    borderColor: '#c0c0c0',
                                    backgroundColor: 'rgba(0,0,0,0.02)'
                                }
                            }}
                        >
                            Reset to All Time
                        </Button>

                        <Button
                            onClick={handleApplyDateRange}
                            variant="contained"
                            size="small"
                            disabled={!tempDateRange[0] || !tempDateRange[1]}
                            sx={{
                                backgroundColor: '#000',
                                color: '#fff',
                                textTransform: 'none',
                                fontSize: '13px',
                                borderRadius: '20px',
                                '&:hover': {
                                    backgroundColor: '#333'
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: '#e0e0e0',
                                    color: '#a0a0a0'
                                }
                            }}
                        >
                            Apply Filter
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </>
    );
};

CalendarFilter.propTypes = {
    onDateRangeChange: PropTypes.func,
    initialDateRange: PropTypes.array
};

export default CalendarFilter;
